import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePermissionsStore } from '@/stores/permissions'
import { useToast } from '@/composables/useToast'

// Views
import LoginView from '@/views/LoginView.vue'
import ForgotPasswordView from '@/views/ForgotPasswordView.vue'
import ResetPasswordView from '@/views/ResetPasswordView.vue'
import OAuthCallbackView from '@/views/OAuthCallbackView.vue'
import DashboardLayout from '@/components/Layout/DashboardLayout.vue'

// Lazy loaded views
const OperatorDashboard = () => import('@/views/Dashboard/OperatorDashboard.vue')
const SupervisorDashboard = () => import('@/views/Dashboard/SupervisorDashboard.vue')
const LeadsListView = () => import('@/views/Leads/LeadsListView.vue')
const LeadDetailView = () => import('@/views/Leads/LeadDetailView.vue')
const LeadFormView = () => import('@/views/Leads/LeadFormView.vue')
const CampaignsListView = () => import('@/views/Campaigns/CampaignsListView.vue')
const CampaignDetailView = () => import('@/views/Campaigns/CampaignDetailView.vue')
const CallsListView = () => import('@/views/Calls/CallsListView.vue')
const GeneratorDashboard = () => import('@/views/Generator/GeneratorDashboard.vue')
const UsersListView = () => import('@/views/Users/UsersListView.vue')
const AuditLogsView = () => import('@/views/Admin/AuditLogsView.vue')
const HelpView = () => import('@/views/HelpView.vue')
const NotFoundView = () => import('@/views/NotFoundView.vue')
const ImportLeadsView = () => import('@/views/ImportLeadsView.vue')
const AnalyticsDashboardView = () => import('@/views/AnalyticsDashboardView.vue')
const SettingsView = () => import('@/views/Admin/SettingsView.vue')
const ZonasListView = () => import('@/views/zonas/ZonasListView.vue')
const TarifaListView = () => import('@/views/tarifas/TarifaListView.vue')
const TemplatesView = () => import('@/views/Admin/TemplatesView.vue')
const RetentionView = () => import('@/views/Admin/RetentionView.vue')
const SystemMonitorView = () => import('@/views/Admin/SystemMonitorView.vue')
const MyLeadsView = () => import('@/views/Leads/MyLeadsView.vue')
const MyCallbacksView = () => import('@/views/Leads/MyCallbacksView.vue')
const LeadsCalendarView = () => import('@/views/Leads/CalendarView.vue')
const LeadsDashboardView = () => import('@/views/Dashboard/LeadsDashboardView.vue')
const RolesAdmin = () => import('@/views/Admin/RolesAdmin.vue')
const PermissionsMatrix = () => import('@/views/Admin/PermissionsMatrix.vue')

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: { public: true },
    },
    {
      path: '/oauth/callback',
      name: 'oauth-callback',
      component: OAuthCallbackView,
      meta: { public: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPasswordView,
      meta: { public: true },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: ResetPasswordView,
      meta: { public: true },
    },
    {
      path: '/',
      component: DashboardLayout,
      children: [
        {
          path: '',
          redirect: (to) => {
            const permissionsStore = usePermissionsStore()
            if (permissionsStore.can('dashboard:view_supervisor')) {
              return { name: 'supervisor-dashboard' }
            }
            if (permissionsStore.can('generator:view_own_leads')) {
              return { name: 'generator-dashboard' }
            }
            return { name: 'operator-dashboard' }
          },
        },
        {
          path: 'dashboard/operator',
          name: 'operator-dashboard',
          component: OperatorDashboard,
          meta: { permission: 'dashboard:view_operator' },
        },
        {
          path: 'dashboard/supervisor',
          name: 'supervisor-dashboard',
          component: SupervisorDashboard,
          meta: { permission: ['dashboard:view_supervisor', 'dashboard:view_live'] },
        },
        {
          path: 'dashboard/live',
          redirect: { name: 'supervisor-dashboard' },
        },
        {
          path: 'leads',
          name: 'leads-list',
          component: LeadsListView,
          meta: { permission: 'leads:read' },
        },
        {
          path: 'leads/new',
          name: 'lead-create',
          component: LeadFormView,
          meta: { permission: 'leads:create' },
        },
        {
          path: 'leads/:id',
          name: 'lead-detail',
          component: LeadDetailView,
          meta: { permission: 'leads:read' },
        },
        {
          path: 'leads/:id/edit',
          name: 'lead-edit',
          component: LeadFormView,
          meta: { permission: 'leads:update' },
        },
        // My routes (for operators)
        {
          path: 'my/leads',
          name: 'my-leads',
          component: MyLeadsView,
          meta: { permission: 'leads:read' },
        },
        {
          path: 'my/callbacks',
          name: 'my-callbacks',
          component: MyCallbacksView,
          meta: { permission: 'leads:read' },
        },
        {
          path: 'my/calendar',
          name: 'my-calendar',
          redirect: { name: 'calendar' },
        },
        {
          path: 'campaigns',
          name: 'campaigns-list',
          component: CampaignsListView,
          meta: { permission: 'campaigns:view' },
        },
        {
          path: 'campaigns/:id',
          name: 'campaign-detail',
          component: CampaignDetailView,
          meta: { permission: 'campaigns:view' },
        },
        {
          path: 'calls',
          name: 'calls-list',
          component: CallsListView,
          meta: { permission: 'call_logs:read' },
        },
        {
          path: 'users',
          name: 'users-list',
          component: UsersListView,
          meta: { permission: 'admin:users:read' },
        },
        // Admin / System Management routes
        {
          path: 'admin/callback-reminders',
          name: 'admin-callback-reminders',
          component: () => import('@/views/Admin/CallbackRemindersAdmin.vue'),
          meta: { permission: 'admin:callback_reminders:read' },
        },
        {
          path: 'admin/call-results',
          name: 'admin-call-results',
          component: () => import('@/views/Admin/CallResultsAdmin.vue'),
          meta: { permission: 'admin:call_results:read' },
        },
        {
          path: 'admin/lead-statuses',
          name: 'admin-lead-statuses',
          component: () => import('@/views/Admin/LeadStatusAdmin.vue'),
          meta: { permission: 'admin:lead_status:read' },
        },
        {
          path: 'admin/settings',
          name: 'admin-settings',
          component: SettingsView,
          meta: { permission: 'admin:settings:read' },
        },
        {
          path: 'tarifas',
          name: 'tarifas-list',
          component: TarifaListView,
          meta: { permission: 'tariffs:read' },
        },
        {
          path: 'zonas',
          name: 'zonas-list',
          component: ZonasListView,
          meta: { permission: 'admin:zonas:read' },
        },
        {
          path: 'admin/templates',
          name: 'admin-templates',
          component: TemplatesView,
          meta: { permission: 'admin:templates:read' },
        },
        {
          path: 'admin/retention',
          name: 'admin-retention',
          component: RetentionView,
          meta: { permission: 'admin:retention:read' },
        },
        {
          path: 'admin/audit',
          name: 'admin-audit',
          component: AuditLogsView,
          meta: { permission: 'admin:audit:read' },
        },
        {
          path: 'admin/system',
          name: 'admin-system',
          component: SystemMonitorView,
          meta: { permission: 'admin:system:view' },
        },
        {
          path: 'admin/roles',
          name: 'admin-roles',
          component: RolesAdmin,
          meta: { permission: 'admin:rbac' },
        },
        {
          path: 'admin/permissions',
          name: 'admin-permissions',
          component: PermissionsMatrix,
          meta: { permission: 'admin:rbac' },
        },
        {
          path: 'generator/dashboard',
          name: 'generator-dashboard',
          component: GeneratorDashboard,
          meta: { permission: 'generator:view_own_leads' },
        },
        {
          path: 'help',
          name: 'help',
          component: HelpView,
        },
        {
          path: 'calendar',
          name: 'calendar',
          component: LeadsCalendarView,
          meta: { permission: 'calendar:read' },
        },
        {
          path: 'leads/import',
          name: 'leads-import',
          component: ImportLeadsView,
          meta: { permission: 'leads:import' },
        },
        {
          path: 'analytics',
          name: 'analytics',
          component: AnalyticsDashboardView,
          meta: { permission: 'analytics:view' },
        },
        {
          path: 'cuadro-mando/leads',
          name: 'leads-dashboard',
          component: LeadsDashboardView,
          meta: { permission: 'analytics:view_leads' },
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: NotFoundView,
    },
  ],
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const permissionsStore = usePermissionsStore()
  const toast = useToast()

  // Hydrate permissions from localStorage if needed (no await - quick sync call)

  // Restore permissions from localStorage if available (avoids permission flicker on refresh)
  if (!permissionsStore.lastFetch) {
    permissionsStore.hydrate()
  }

  // Check if route is public
  if (to.meta.public) {
    if (authStore.isAuthenticated) {
      // Already logged in, redirect based on permission
      if (permissionsStore.can('dashboard:view_supervisor')) {
        next({ name: 'supervisor-dashboard' })
      } else if (permissionsStore.can('generator:view_own_leads')) {
        next({ name: 'generator-dashboard' })
      } else {
        next({ name: 'operator-dashboard' })
      }
    } else {
      next()
    }
    return
  }

  // Check authentication
  if (!authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }

  // Check permission
  if (to.meta.permission) {
    const required = to.meta.permission as string | string[]
    if (!permissionsStore.canAny(required)) {
      toast.error('No tienes permiso para acceder a esta página')
      next({ path: '/' })
      return
    }
  }

  next()
})

export default router
